import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "../components/layout.tsx";
import { useAuth } from "../context/auth.tsx";
import { getDeliveryOverview, type DeliveryOverview } from "../api/delivery.ts";

export function DeliveryOverviewPage() {
  const { token } = useAuth();
  const [data, setData] = useState<DeliveryOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (token == null) return;
    setLoading(true);
    getDeliveryOverview(token)
      .then(setData)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, [token]);

  const sum = (obj: Record<string, number> | undefined) => Object.values(obj ?? {}).reduce((a, b) => a + b, 0);

  const cards = [
    { label: "Requirements", count: sum(data?.requirements), to: "/delivery/requirements", color: "border-blue-500/30 bg-blue-900/10" },
    { label: "Active Tasks", count: data?.tasks["IN_PROGRESS"] ?? 0, to: "/delivery/tasks?status=IN_PROGRESS", color: "border-green-500/30 bg-green-900/10" },
    { label: "Total Tasks", count: sum(data?.tasks), to: "/delivery/tasks", color: "border-teal-500/30 bg-teal-900/10" },
    { label: "Sprints", count: sum(data?.sprints), to: "/delivery/sprints", color: "border-purple-500/30 bg-purple-900/10" },
    { label: "Pending Reviews", count: (data?.reviews["PENDING"] ?? 0) + (data?.reviews["IN_REVIEW"] ?? 0), to: "/delivery/reviews", color: "border-yellow-500/30 bg-yellow-900/10" },
    { label: "Approved Reviews", count: data?.reviews["APPROVED"] ?? 0, to: "/delivery/reviews?status=APPROVED", color: "border-emerald-500/30 bg-emerald-900/10" },
  ];

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Delivery Overview</h1>
          <div className="flex gap-2">
            <Link to="/delivery/requirements" className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors">+ Requirement</Link>
            <Link to="/delivery/tasks" className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-colors">+ Task</Link>
          </div>
        </div>

        {loading && <p className="text-gray-400">Loading…</p>}
        {error != null && <p className="text-red-400">{error}</p>}

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {cards.map((card) => (
            <Link key={card.label} to={card.to} className={`block rounded-lg border p-5 transition-all hover:brightness-110 ${card.color}`}>
              <p className="text-gray-400 text-sm">{card.label}</p>
              <p className="text-3xl font-bold text-white mt-1">{card.count}</p>
            </Link>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-4">
            <h2 className="text-white font-semibold mb-3">Quick Links</h2>
            <nav className="space-y-1">
              {[
                { to: "/delivery/sprints", label: "Sprint Management" },
                { to: "/delivery/milestones", label: "Milestones" },
                { to: "/delivery/reviews", label: "Code Reviews" },
                { to: "/delivery/docs", label: "Documentation" },
                { to: "/delivery/qa", label: "QA & Testing" },
                { to: "/delivery/release", label: "Release Preparation" },
              ].map(({ to, label }) => (
                <Link key={to} to={to} className="block px-3 py-2 rounded text-gray-300 hover:text-white hover:bg-gray-700 text-sm transition-colors">{label}</Link>
              ))}
            </nav>
          </div>
          <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-4">
            <h2 className="text-white font-semibold mb-3">Task Distribution</h2>
            {data == null ? <p className="text-gray-400 text-sm">No data</p> : (
              <div className="space-y-2">
                {Object.entries(data.tasks).map(([status, count]) => (
                  <div key={status} className="flex justify-between text-sm">
                    <span className="text-gray-400">{status}</span>
                    <span className="text-white font-medium">{count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
